import React, { FC, useCallback, useMemo } from "react";

import Dropdown from "antd/es/dropdown";
import { MenuProps } from "antd/es/menu";
import { MenuInfo } from "rc-menu/lib/interface";
import { useIntl } from "react-intl";

import { TripType } from "@mapmycustomers/shared";
import FilterOperator from "@mapmycustomers/shared/enum/FilterOperator";

import RoundButton from "@app/component/RoundButton";
import useView from "@app/component/view/utils/useView";
import useAnalytics from "@app/util/contexts/useAnalytics";
import { TripFieldName } from "@app/util/fieldModel/tripFieldModel";
import getTripTypeDisplayName from "@app/util/ui/getTripTypeDisplayName";
import { isSimpleCondition } from "@app/util/viewModel/assert";

import styles from "./CommonFilterStyles.module.scss";

const CLEAR_OPTION_VALUE = "clear";
const UNCLASSIFIED_OPTION_VALUE = "unclassified";

export interface TypeFilterProps {}

const TypeFilter: FC<TypeFilterProps> = () => {
  const intl = useIntl();
  const analytics = useAnalytics();

  const {
    updateFilterModel,
    viewState: { filter },
  } = useView();

  const condition = filter[TripFieldName.TYPE];
  const value = useMemo(() => {
    if (!condition || !isSimpleCondition(condition)) {
      return undefined;
    }
    if (condition.operator === FilterOperator.EQUALS) {
      return condition.value as TripType;
    } else if (condition.operator === FilterOperator.EMPTY) {
      return UNCLASSIFIED_OPTION_VALUE;
    }
    return undefined;
  }, [condition]);

  const handleMenuClick = useCallback(
    (info: MenuInfo) => {
      analytics.using(["Type Filter"]);
      const value = info.key as
        | null
        | TripType
        | typeof CLEAR_OPTION_VALUE
        | typeof UNCLASSIFIED_OPTION_VALUE;
      if (value === CLEAR_OPTION_VALUE) {
        updateFilterModel({ [TripFieldName.TYPE]: undefined });
      } else if (value === UNCLASSIFIED_OPTION_VALUE) {
        updateFilterModel({
          [TripFieldName.TYPE]: { operator: FilterOperator.EMPTY, value: undefined },
        });
      } else {
        updateFilterModel({ [TripFieldName.TYPE]: { operator: FilterOperator.EQUALS, value } });
      }
    },
    [analytics, updateFilterModel]
  );

  const menuProps = useMemo(() => {
    const items: MenuProps["items"] = [
      {
        className: styles.clearMenuItem,
        key: CLEAR_OPTION_VALUE,
        label: intl.formatMessage({
          id: "reports.mileageTracking.filters.type.options.clear",
          defaultMessage: "Clear",
          description: "Clear option in the Type filter button on the Mileage Tracking page",
        }),
      },
      ...[TripType.BUSINESS, TripType.PERSONAL, null].map((tripType) => ({
        key: tripType ?? UNCLASSIFIED_OPTION_VALUE,
        label: getTripTypeDisplayName(intl, tripType),
      })),
    ];
    return {
      activeKey: value,
      items,
      onClick: handleMenuClick,
    };
  }, [handleMenuClick, intl, value]);

  return (
    <Dropdown menu={menuProps} overlayClassName={styles.dropdownOverlay} trigger={["click"]}>
      <RoundButton active={!!value} hasMenu>
        {intl.formatMessage(
          {
            id: "reports.mileageTracking.filters.type.title",
            defaultMessage: "Type{hasValue, select, true {: {value}} other {}}",
            description: "Title of the Type filter button on the Mileage Tracking page",
          },
          {
            hasValue: !!value,
            value: value
              ? getTripTypeDisplayName(intl, value === UNCLASSIFIED_OPTION_VALUE ? null : value)
              : undefined,
          }
        )}
      </RoundButton>
    </Dropdown>
  );
};

export default TypeFilter;
